import React, { useEffect, useState } from 'react';
import { 
  Box, Button, Typography, Card, CardContent, CardActions, Grid, Chip, 
  Dialog, DialogTitle, DialogContent, TextField, DialogActions,
  FormControl, InputLabel, Select, MenuItem, FormHelperText 
} from '@mui/material';
import { supabase } from '../../Supabase/supabaseRealtimeClient';
import { VideoAtendimento, PrioridadeAtendimento } from '../../types/telemedicina';
import VideoRoom from './VideoRoom';

const TelemedicinaTriagem: React.FC = () => {
  const [chamados, setChamados] = useState<VideoAtendimento[]>([]);
  const [activeCall, setActiveCall] = useState<VideoAtendimento | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [obsTriagem, setObsTriagem] = useState('');
  const [prioridade, setPrioridade] = useState<PrioridadeAtendimento>('Não urgente');
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    fetchChamados();
    getUserId();

    const channel = supabase
      .channel('public:video_atendimentos_triagem')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'video_atendimentos' },
        (payload: any) => {
          console.log('[TelemedicinaTriagem] Change received:', payload);
          // O som é gerenciado globalmente pelo NotificationService
          // Refresh list on any change
          fetchChamados();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getUserId = async () => {
    let { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        if (!error && data.user) {
          user = data.user;
        }
      }
    }

    if (user) setUserId(user.id);
  };

  const fetchChamados = async () => {
    const { data } = await supabase
      .from('video_atendimentos')
      .select('*, cliente:cliente(nome, telefone)')
      .in('status', ['aguardando_triagem', 'em_triagem'])
      .order('data_abertura', { ascending: true });
    
    if (data) setChamados(data);
  };

  const handleAtender = async (chamado: VideoAtendimento) => {
    console.log('[TelemedicinaTriagem] Tentando iniciar atendimento', { chamadoId: chamado.id, userId });
    
    if (!userId) {
      alert('Erro: Usuário não identificado. Tente recarregar a página.');
      console.error('[TelemedicinaTriagem] UserId is null');
      return;
    }
    
    // Update status to 'em_triagem' and set atendente_id
    const { data, error } = await supabase
      .from('video_atendimentos')
      .update({ 
        status: 'em_triagem', 
        atendente_id: userId,
        data_inicio_triagem: new Date().toISOString()
      })
      .eq('id', chamado.id)
      .eq('status', 'aguardando_triagem') // Safety check
      .select();

    if (!error && data && data.length > 0) {
      console.log('[TelemedicinaTriagem] Atendimento iniciado com sucesso');
      setActiveCall({ ...chamado, status: 'em_triagem' });
    } else if (!error && (!data || data.length === 0)) {
       alert('Este chamado já foi atendido por outro profissional ou cancelado.');
       fetchChamados(); // Refresh list
    } else {
      console.error('[TelemedicinaTriagem] Erro ao iniciar atendimento:', error);
      alert(`Erro ao iniciar atendimento: ${error?.message || 'Erro desconhecido'}`);
    }
  };

  const handleOpenEncaminhar = () => {
    setPrioridade('Não urgente');
    setOpenDialog(true);
  };

  const handleEncaminharMedico = async () => {
    if (!activeCall) return;

    const { error } = await supabase
      .from('video_atendimentos')
      .update({
        status: 'aguardando_medico',
        observacoes_triagem: obsTriagem,
        prioridade: prioridade
      })
      .eq('id', activeCall.id);

    if (!error) {
      setOpenDialog(false);
      setActiveCall(null);
      setObsTriagem('');
    } else {
      alert('Erro ao encaminhar');
    }
  };

  const handleFinalizarSemMedico = async () => {
     if (!activeCall) return;
     const { error } = await supabase
      .from('video_atendimentos')
      .update({
        status: 'finalizado',
        observacoes_triagem: obsTriagem
      })
      .eq('id', activeCall.id);

    if (!error) {
      setOpenDialog(false);
      setActiveCall(null);
      setObsTriagem('');
    }
  };

  const handleEndCall = async () => {
    if (!activeCall) return;
    
    // Encerrar diretamente, talvez com uma observação padrão se estiver vazia
    const finalObs = obsTriagem || 'Encerrado pelo atendente via vídeo.';

    const { error } = await supabase
     .from('video_atendimentos')
     .update({
       status: 'finalizado',
       observacoes_triagem: finalObs,
       data_fechamento: new Date().toISOString()
     })
     .eq('id', activeCall.id);

   if (!error) {
     setActiveCall(null);
     setObsTriagem('');
   } else {
     alert(`Erro ao encerrar: ${error.message}`);
   }
 };

 if (activeCall && userId) {
    return (
      <Box sx={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1 }}>
           <VideoRoom 
            roomId={`room-${activeCall.id}`}
            userId={userId} 
            onLeave={() => setActiveCall(null)} 
            onEndCall={handleEndCall}
          >
            <Button 
              variant="contained" 
              color="warning" 
              fullWidth 
              onClick={handleOpenEncaminhar}
              // sx={{ py: 1.5, fontWeight: 'bold' }}
            >
              Encaminhar / Finalizar Triagem
            </Button>
          </VideoRoom>
        </Box>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Finalizar Triagem</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Observações da Triagem"
              fullWidth
              multiline
              rows={4}
              value={obsTriagem}
              onChange={(e) => setObsTriagem(e.target.value)}
            />
            
            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
                <InputLabel id="prioridade-label">Prioridade</InputLabel>
                <Select
                    labelId="prioridade-label"
                    value={prioridade}
                    label="Prioridade"
                    onChange={(e) => setPrioridade(e.target.value as PrioridadeAtendimento)}
                >
                    <MenuItem value="Não urgente">Não urgente (Azul)</MenuItem>
                    <MenuItem value="Pouco Urgente">Pouco Urgente (Verde)</MenuItem>
                    <MenuItem value="Urgente">Urgente (Amarelo)</MenuItem>
                    <MenuItem value="Muito Urgente">Muito Urgente (Laranja)</MenuItem>
                    <MenuItem value="Emergencia">Emergência (Vermelho)</MenuItem>
                </Select>
                <FormHelperText>Selecione a gravidade do caso para a fila médica</FormHelperText>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFinalizarSemMedico} color="primary">
              Finalizar (Alta)
            </Button>
            <Button onClick={handleEncaminharMedico} variant="contained" color="secondary">
              Encaminhar para Médico
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Fila de Triagem</Typography>
      <Grid container spacing={2}>
        {chamados.map((chamado) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={chamado.id}>
            <Card sx={{ bgcolor: chamado.status === 'em_triagem' ? '#fff3e0' : 'white' }}>
              <CardContent>
                <Typography variant="h6">
                  {/* @ts-ignore */}
                  {chamado.cliente?.nome || 'Cliente Desconhecido'}
                </Typography>
                <Chip 
                  label={chamado.status.replace('_', ' ')} 
                  color={chamado.status === 'em_triagem' ? 'warning' : 'default'} 
                  size="small" 
                  sx={{ my: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Queixa: {chamado.queixa_inicial}
                </Typography>
                <Typography variant="caption" display="block">
                  {new Date(chamado.data_abertura).toLocaleString()}
                </Typography>
              </CardContent>
              <CardActions>
                {chamado.status === 'aguardando_triagem' && (
                  <Button size="small" variant="contained" onClick={() => handleAtender(chamado)}>
                    Iniciar Triagem
                  </Button>
                )}
                {chamado.status === 'em_triagem' && chamado.atendente_id === userId && (
                   <Button size="small" variant="contained" color="warning" onClick={() => setActiveCall(chamado)}>
                    Retomar Chamada
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
        {chamados.length === 0 && (
          <Typography sx={{ p: 2 }}>Nenhum paciente na fila.</Typography>
        )}
      </Grid>
    </Box>
  );
};

export default TelemedicinaTriagem;
